import { createSavedPrompt } from "@Api/PromptLibrary/createSavedPrompt";
import { deleteSavedPrompt } from "@Api/PromptLibrary/deleteSavedPrompt";
import { getSavedPrompts } from "@Api/PromptLibrary/getSavedPrompts";
import { updateSavedPrompt } from "@Api/PromptLibrary/updateSavedPrompt";
import { CenteredSpinner, Screen } from "@Components/Page";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { Banner, Button, EmptyState, ErrorIcon, Lede, Sheet, Spinner } from "@UI";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PromptEdit } from "./Edit";
import { PromptOverview } from "./Overview";
function placePrompt(list, prompt, type) {
    if (prompt.type !== type) return list.filter((p)=>p.id !== prompt.id);
    return list.some((p)=>p.id === prompt.id) ? list.map((p)=>p.id === prompt.id ? prompt : p) : [
        ...list,
        prompt
    ];
}
export function PromptLibrary({ onUsePrompt }) {
    const { t } = useTranslation();
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const [isLoading, setIsLoading] = React.useState(false);
    const [hasLoadError, setHasLoadError] = React.useState(false);
    const [loadAttempt, setLoadAttempt] = React.useState(0);
    const [privatePrompts, setPrivatePrompts] = React.useState([]);
    const [teamPrompts, setTeamPrompts] = React.useState([]);
    const [juristicPrompts, setJuristicPrompts] = React.useState([]);
    const [promptType, setPromptType] = React.useState("private");
    const [prompt, setPrompt] = React.useState(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [hasSaveError, setHasSaveError] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [hasDeleteError, setHasDeleteError] = React.useState(false);
    const favouriteRequests = useRef(new Map());
    const favouriteBase = useRef(new Map());
    const openedPrompt = useRef(0);
    useEffect(()=>{
        if (!token) return;
        setIsLoading(true);
        setHasLoadError(false);
        getSavedPrompts(token).then((prompts)=>{
            setPrivatePrompts(prompts.filter((p)=>p.type === "private"));
            setTeamPrompts(prompts.filter((p)=>p.type === "organization"));
            setJuristicPrompts(prompts.filter((p)=>p.type === "juristic"));
        }).catch(()=>setHasLoadError(true)).finally(()=>setIsLoading(false));
    }, [
        loadAttempt
    ]);
    const applySaved = useCallback((saved)=>{
        setPrivatePrompts((prev)=>placePrompt(prev, saved, "private"));
        setTeamPrompts((prev)=>placePrompt(prev, saved, "organization"));
    }, []);
    const openPrompt = useCallback((next)=>{
        openedPrompt.current += 1;
        setHasSaveError(false);
        setPrompt(next);
    }, []);
    const onNewPrompt = useCallback(()=>{
        openPrompt({
            isFavourite: false,
            type: promptType,
            prompt: "",
            title: ""
        });
    }, [
        promptType,
        openPrompt
    ]);
    const onFavouritePrompt = useCallback((original)=>{
        if (!token) return;
        const toggled = {
            ...original,
            isFavourite: !original.isFavourite
        };
        const request = (favouriteRequests.current.get(original.id) ?? 0) + 1;
        favouriteRequests.current.set(original.id, request);
        if (!favouriteBase.current.has(original.id)) favouriteBase.current.set(original.id, original);
        const isLatest = ()=>favouriteRequests.current.get(original.id) === request;
        const settle = (next)=>{
            if (!isLatest()) return;
            favouriteBase.current.delete(original.id);
            applySaved(next);
        };
        applySaved(toggled);
        updateSavedPrompt(token, original.id, toggled).then(settle).catch(()=>settle(favouriteBase.current.get(original.id) ?? original));
    }, [
        token,
        applySaved
    ]);
    const onSavePrompt = useCallback((p)=>{
        if (!token || isSaving) return;
        setIsSaving(true);
        setHasSaveError(false);
        const opened = openedPrompt.current;
        const isStillOpen = ()=>openedPrompt.current === opened;
        const request = p.id ? updateSavedPrompt(token, p.id, p) : createSavedPrompt(token, p);
        request.then((data)=>{
            applySaved(data);
            if (isStillOpen()) setPrompt(null);
        }).catch(()=>isStillOpen() && setHasSaveError(true)).finally(()=>setIsSaving(false));
    }, [
        token,
        isSaving,
        applySaved
    ]);
    const onDeletePrompt = useCallback((target)=>{
        setHasDeleteError(false);
        setDeleteTarget(target);
    }, []);
    const onConfirmDelete = useCallback(()=>{
        if (!token || !deleteTarget || isDeleting) return;
        const { id } = deleteTarget;
        setIsDeleting(true);
        setHasDeleteError(false);
        deleteSavedPrompt(token, id).then(()=>{
            favouriteRequests.current.set(id, (favouriteRequests.current.get(id) ?? 0) + 1);
            setPrivatePrompts((prev)=>prev.filter((p)=>p.id !== id));
            setTeamPrompts((prev)=>prev.filter((p)=>p.id !== id));
            setDeleteTarget(null);
        }).catch(()=>setHasDeleteError(true)).finally(()=>setIsDeleting(false));
    }, [
        token,
        deleteTarget,
        isDeleting
    ]);
    const closeDelete = ()=>!isDeleting && setDeleteTarget(null);
    if (isLoading) {
        return /*#__PURE__*/ React.createElement(CenteredSpinner, null, /*#__PURE__*/ React.createElement(Spinner, {
            size: "lg"
        }));
    }
    if (hasLoadError) {
        return /*#__PURE__*/ React.createElement(EmptyState, {
            icon: /*#__PURE__*/ React.createElement(ErrorIcon, null),
            tone: "neutral",
            text: t("prompts.loadFailed"),
            actions: /*#__PURE__*/ React.createElement(Button, {
                variant: "tonal",
                size: "lg",
                onClick: ()=>setLoadAttempt((count)=>count + 1)
            }, t("generic.retry"))
        });
    }
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Screen, {
        hidden: !!prompt
    }, /*#__PURE__*/ React.createElement(PromptOverview, {
        type: promptType,
        onChangePromptType: setPromptType,
        onEditPrompt: openPrompt,
        onUsePrompt: onUsePrompt,
        onFavouritePrompt: onFavouritePrompt,
        onDeletePrompt: onDeletePrompt,
        onNewPrompt: onNewPrompt,
        privatePrompts: privatePrompts,
        teamPrompts: teamPrompts,
        juristicPrompts: juristicPrompts
    })), /*#__PURE__*/ React.createElement(Sheet, {
        open: !!deleteTarget,
        onClose: closeDelete,
        placement: "center",
        title: t("prompts.deleteConfirm"),
        leftAction: /*#__PURE__*/ React.createElement(Button, {
            variant: "outline",
            size: "lg",
            disabled: isDeleting,
            onClick: closeDelete
        }, t("generic.cancel")),
        footer: /*#__PURE__*/ React.createElement(Button, {
            variant: "danger",
            size: "lg",
            loading: isDeleting,
            onClick: onConfirmDelete
        }, t("generic.delete"))
    }, hasDeleteError && /*#__PURE__*/ React.createElement(Banner, {
        tone: "error",
        inline: true
    }, t("prompts.deleteFailed")), /*#__PURE__*/ React.createElement(Lede, null, t("prompts.deleteText"))), prompt && /*#__PURE__*/ React.createElement(PromptEdit, {
        prompt: prompt,
        setPrompt: setPrompt,
        onSave: onSavePrompt,
        onStop: ()=>openPrompt(null),
        onUse: onUsePrompt,
        isEditable: promptType !== "juristic",
        isSaving: isSaving,
        hasSaveError: hasSaveError
    }));
}
